import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage, useIntl } from 'react-intl';

import Grid from '@material-ui/core/Grid';

import { Table } from 'components/common';
import { DynoSelectionSection } from 'components/dyno-template';
import TransferFavouriteContainer from 'container/transfer/Favourite';

import { get } from 'utils/lodash';
import { findArrayEntry } from 'utils/array';
import { getSwiftBanks } from 'utils/transfer/swift/getter';
import { getBeneficiaryBankLabels } from 'utils/transfer/getter';
import { isSwiftBankEmpty } from 'utils/transfer/swift/validation';
import { autocompletePlaceholderHandler } from 'utils/transfer/handler';
import { deleteFavouriteHandler, editFavouriteSelectionHandler } from 'utils/favourite/transfer/handler';

import { SWIFT_PAGE_SIZE } from 'settings/constants/ui-control';
import { SWIFT_TABLE_HEADERS } from 'settings/constants/transfer/swift';
import { OWN_ACCOUNT, SWIFT_TRANSFER } from 'settings/constants/transaction';
import { CURRENCY_IDR, MULTICURRENCY_PRODUCT_TYPE } from 'settings/constants/common';

const AccountSelectionSection = ({
  userId,
  onChange,
  swiftBank,
  isFormOpen,
  searchValue,
  onCodeClick,
  transferType,
  onSearchBank,
  transferTypes,
  onAddFavourite,
  senderAccount,
  senderAccounts,
  transferDetail,
  isServiceOnline,
  transactionType,
  onShowFormModal,
  favouriteAccount,
  receiverAccounts,
  senderAccountValue,
  onOwnAccountChange,
  isTransactionBlocked,
  onTransferFromFavourite,
  onTransferTypeChange,
  favouriteTransferType,
  onFavouriteTypeChange,
}) => {
  const { formatMessage } = useIntl();

  const productCode = get(senderAccount, 'productCode', '');
  const isNonIDR = get(senderAccount, 'currency', '') !== CURRENCY_IDR;
  const banks = MULTICURRENCY_PRODUCT_TYPE.includes(productCode) && isNonIDR ? [] : get(transferDetail, 'transferBanks', []);
  const bankLabels = getBeneficiaryBankLabels(banks);

  const hasSwiftBanks = transferType === SWIFT_TRANSFER && !isSwiftBankEmpty(swiftBank);
  const swiftBanks = !isSwiftBankEmpty(swiftBank) && getSwiftBanks(swiftBank, onCodeClick);

  // Only applicable for SWIFT tranfer at the moment
  const isSearchSectionDisplay = transferType === SWIFT_TRANSFER && isServiceOnline;
  // Only applicable for own account transfer
  const isOwnAccountFavouriteDisplay = transferType === OWN_ACCOUNT && isServiceOnline && !isEmpty(receiverAccounts);

  const optionPlaceholder = autocompletePlaceholderHandler(transferType);

  const handleAutocompleteChange = value => {
    const selectedBank = findArrayEntry(banks, 'label', value);
    onChange({ beneficiaryBank: selectedBank });
    onShowFormModal();
  };

  const handleKeywordChange = e => onChange({ searchValue: e.target.value });

  const handleDeleteFavourite = payload => deleteFavouriteHandler(userId, payload);

  const handleEditFavourite = payload => editFavouriteSelectionHandler({ userId, ...payload });

  return (
    <>
      <Grid item xs={12} md={10} xl={8} className="account-selector--container pv4 ph3">
        <DynoSelectionSection
          type={transactionType}
          optionLabels={bankLabels}
          onSearchBank={onSearchBank}
          hasOptions={!isEmpty(banks)}
          sourceAccount={senderAccount}
          sourceAccounts={senderAccounts}
          transactionType={transferType}
          transactionTypes={transferTypes}
          isServiceOnline={isServiceOnline}
          optionPlaceholder={optionPlaceholder}
          sourceAccountValue={senderAccountValue}
          isSearchSectionDisplay={isSearchSectionDisplay}
          onAutocompleteChange={handleAutocompleteChange}
          onTransactionTypeChange={onTransferTypeChange}
          toLabel={<FormattedMessage id="transaction.transfer.to" />}
          fromLabel={<FormattedMessage id="transaction.transfer.from" />}
          onSourceAccountChange={e => onChange({ transferFrom: e.target.value })}
          receiverAccounts={receiverAccounts}
        />
      </Grid>

      <Grid container justify="center" className="account-favorite--container">
        {hasSwiftBanks ? (
          <div className="pa3 pa4-l w-100">
            <Table columns={SWIFT_TABLE_HEADERS(formatMessage)} rows={swiftBanks} pageSize={SWIFT_PAGE_SIZE} hasPagination />
          </div>
        ) : (
          !isTransactionBlocked && (
            <TransferFavouriteContainer
              userId={userId}
              isFormOpen={isFormOpen}
              searchValue={searchValue}
              onAddFavourite={onAddFavourite}
              transactionType={transactionType}
              senderAccount={senderAccount}
              receiverAccounts={receiverAccounts}
              favouriteAccount={favouriteAccount}
              onEditFavourite={handleEditFavourite}
              onKeywordChange={handleKeywordChange}
              onOwnAccountChange={onOwnAccountChange}
              onDeleteFavourite={handleDeleteFavourite}
              onTransferTypeChange={onTransferTypeChange}
              favouriteTransferType={favouriteTransferType}
              onFavouriteTypeChange={onFavouriteTypeChange}
              onTransferFromFavourite={onTransferFromFavourite}
              isOwnAccountFavouriteDisplay={isOwnAccountFavouriteDisplay}
            />
          )
        )}
      </Grid>
    </>
  );
};

AccountSelectionSection.defaultProps = { onOwnAccountChange: null, transferTypes: [], receiverAccounts: [] };

AccountSelectionSection.propTypes = {
  transferTypes: PropTypes.array,
  onOwnAccountChange: PropTypes.func,
  receiverAccounts: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  isFormOpen: PropTypes.bool.isRequired,
  onCodeClick: PropTypes.func.isRequired,
  swiftBank: PropTypes.object.isRequired,
  onSearchBank: PropTypes.func.isRequired,
  searchValue: PropTypes.string.isRequired,
  transferType: PropTypes.string.isRequired,
  onAddFavourite: PropTypes.func.isRequired,
  senderAccount: PropTypes.object.isRequired,
  senderAccounts: PropTypes.array.isRequired,
  isServiceOnline: PropTypes.bool.isRequired,
  onShowFormModal: PropTypes.func.isRequired,
  transferDetail: PropTypes.object.isRequired,
  favouriteAccount: PropTypes.object.isRequired,
  transactionType: PropTypes.string.isRequired,
  senderAccountValue: PropTypes.string.isRequired,
  onTransferTypeChange: PropTypes.func.isRequired,
  isTransactionBlocked: PropTypes.bool.isRequired,
  onFavouriteTypeChange: PropTypes.func.isRequired,
  onTransferFromFavourite: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  favouriteTransferType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default AccountSelectionSection;
